import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import { useAuth } from '@/context/AuthContext';
import { apiRequest } from '@/lib/auth';
import { LESSONS, MODULES } from '@/data/lessons';

type LessonState = {
  mastered?: boolean;
  attempts?: number;
  correct?: number;
  wrong?: number;
  stageIndex?: number;
  stageData?: Record<string, { mastered?: boolean }>;
};

type Student = {
  id: string;
  name: string;
  email: string;
  joinedAt: string;
  lastSavedAt: string | null;
  lastPracticeAt: number | null;
  accessLevel: string;
  mastered: number;
  started: number;
  correct: number;
  wrong: number;
  xp: number;
  attemptRecords: number;
};

type StudentDetail = Student & {
  progress: Record<string, LessonState>;
  recentAttempts: { id?: string; at: number; lessonId: number; result?: string; scope?: string }[];
};

type Overview = {
  total: number;
  registered7d: number;
  registered30d: number;
  practiced7d: number;
  started: number;
  courseCompleted: number;
  dailyRegistrations: { day: string; count: number }[];
  generatedAt: string;
};

type StudentList = {
  total: number;
  items: Student[];
  offset: number;
  limit: number;
};

const PAGE_SIZE = 20;

const dates = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
});
const timestamps = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
  hour: '2-digit', minute: '2-digit',
});

function formatDate(value: string | number | null | undefined, includeTime = false) {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : (includeTime ? timestamps : dates).format(parsed);
}
function percent(value: number) {
  return Math.max(0, Math.min(100, Math.round((value / 48) * 100)));
}
function recentRegistration(value: string) {
  return Date.now() - new Date(value).getTime() < 7 * 24 * 60 * 60 * 1000;
}

function Metric({ label, value, hint, icon }: { label: string; value: number; hint: string; icon: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-xl text-orange-600">
          <i className={icon} />
        </span>
      </div>
      <div className="mt-2 font-heading text-4xl font-extrabold text-slate-950">{value.toLocaleString('pt-BR')}</div>
      <p className="mt-2 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function RegistrationChart({ points }: { points: Overview['dailyRegistrations'] }) {
  const max = Math.max(1, ...points.map((point) => point.count));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-extrabold text-slate-950">Novos cadastros</h2>
          <p className="mt-1 text-sm text-slate-500">Últimos 14 dias · dados reais do cadastro</p>
        </div>
        <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
          {points.reduce((sum, p) => sum + p.count, 0)} no período
        </span>
      </div>
      <div role="img" aria-label="Gráfico de novos cadastros por dia" className="mt-7 flex h-40 items-end gap-1.5 border-b border-slate-200 sm:gap-3">
        {points.map((p) => (
          <div key={p.day} title={`${formatDate(p.day + 'T12:00:00Z')}: ${p.count} cadastros`} className="flex h-full min-w-0 flex-1 flex-col justify-end gap-1">
            <div className="text-center text-[10px] font-bold tabular-nums text-slate-600">{p.count || ''}</div>
            <div className={`w-full rounded-t-md ${p.count ? 'bg-gradient-to-t from-orange-500 to-amber-300' : 'bg-slate-100'}`}
              style={{ height: `${p.count ? Math.max(9, Math.round((p.count / max) * 118)) : 4}px` }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-1.5 sm:gap-3">
        {points.map((p, i) => (
          <div key={p.day} className="min-w-0 flex-1 text-center text-[9px] font-semibold text-slate-400 sm:text-[11px]">
            {i % 2 === 0 || i === points.length - 1 ? p.day.slice(8) : ''}
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">Os números são atualizados automaticamente a cada minuto.</p>
    </div>
  );
}

function StudentCard({ student, onOpen, selected }: { student: Student; onOpen: () => void; selected: boolean }) {
  return (
    <button type="button" onClick={onOpen}
      className={`w-full rounded-2xl border p-4 text-left transition hover:border-orange-300 hover:bg-orange-50/40 ${selected ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-sm text-slate-900">{student.name}</strong>
            {recentRegistration(student.joinedAt) && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">Novo</span>}
            {student.accessLevel === 'admin' && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">Administrador</span>}
          </div>
          <div className="mt-1 break-all text-xs text-slate-500">{student.email}</div>
        </div>
        <span className="shrink-0 text-xs font-bold text-orange-700">Ver evolução <i className="ri-arrow-right-s-line" /></span>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 text-xs">
        <span className="font-semibold text-slate-700">{student.mastered}/48 aulas dominadas</span>
        <span className="text-slate-500">{student.xp.toLocaleString('pt-BR')} XP</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-orange-500" style={{ width: `${percent(student.mastered)}%` }} />
      </div>
      <div className="mt-3 flex flex-wrap justify-between gap-2 text-[11px] text-slate-500">
        <span>Cadastro: {formatDate(student.joinedAt)}</span>
        <span>Última prática registrada: {formatDate(student.lastPracticeAt, true)}</span>
      </div>
    </button>
  );
}

function StudentEvolution({ detail, loading, error, onClose }: {
  detail: StudentDetail | null; loading: boolean; error: string; onClose: () => void;
}) {
  return (
    <section aria-label="Evolução do aluno" className="rounded-2xl border border-orange-200 bg-white p-5 shadow-sm lg:sticky lg:top-24 lg:self-start">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-[11px] font-extrabold uppercase tracking-widest text-orange-600">Ficha do aluno</span>
          <h2 className="mt-1 font-heading text-xl font-extrabold text-slate-950">Evolução individual</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Fechar evolução" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
          <i className="ri-close-line text-lg" />
        </button>
      </div>
      {loading && <p className="mt-5 text-sm text-slate-500">Carregando resultados...</p>}
      {error && <p role="alert" className="mt-5 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {detail && !loading && (
        <>
          <div className="mt-5 rounded-xl bg-slate-50 p-4">
            <strong className="break-words text-base text-slate-900">{detail.name}</strong>
            <div className="mt-1 break-all text-xs text-slate-600">{detail.email}</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[
                { value: detail.mastered, label: 'Dominadas' },
                { value: detail.correct, label: 'Acertos' },
                { value: detail.xp, label: 'XP' },
              ].map((item) => (
                <div key={item.label} className="rounded-lg bg-white p-2">
                  <div className="text-lg font-extrabold tabular-nums text-slate-950">{item.value.toLocaleString('pt-BR')}</div>
                  <div className="text-[10px] font-semibold text-slate-500">{item.label}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">Início: {formatDate(detail.joinedAt)} · Última sincronização: {formatDate(detail.lastSavedAt, true)}</p>
          </div>
          <h3 className="mt-6 font-heading text-sm font-extrabold text-slate-900">Progresso por módulo</h3>
          <div className="mt-3 space-y-3">
            {MODULES.map((module) => {
              const done = module.lessonIds.filter((id) => detail.progress[id]?.mastered === true).length;
              return (
                <div key={module.id}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                    <span className="font-semibold text-slate-700">{module.id}. {module.title}</span>
                    <span className="shrink-0 font-bold text-slate-800">{done}/{module.lessonIds.length}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(100 * done) / module.lessonIds.length}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <details className="mt-6 rounded-xl border border-slate-200 p-3" open>
            <summary className="cursor-pointer text-sm font-extrabold text-slate-900">Detalhes das 48 aulas</summary>
            <div className="mt-3 max-h-[280px] space-y-1 overflow-y-auto pr-1">
              {LESSONS.map((lesson) => {
                const progress = detail.progress[lesson.id];
                const mastered = progress?.mastered === true;
                const started = Boolean(progress?.attempts || progress?.stageIndex || mastered);
                return (
                  <div key={lesson.id} className="flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-xs odd:bg-slate-50">
                    <span className="min-w-0 text-slate-700">{lesson.id}. {lesson.title}
                      {lesson.id <= 4 && started && !mastered && (
                        <span className="ml-1 text-[10px] text-slate-500">· etapa {(progress?.stageIndex ?? 0) + 1}</span>
                      )}
                    </span>
                    <span className={`shrink-0 font-bold ${mastered ? 'text-emerald-700' : started ? 'text-orange-700' : 'text-slate-400'}`}>
                      {mastered ? 'Dominada' : started ? 'Em estudo' : 'Não iniciada'}
                    </span>
                  </div>
                );
              })}
            </div>
          </details>
          <h3 className="mt-6 font-heading text-sm font-extrabold text-slate-900">Histórico recente</h3>
          <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
            {detail.recentAttempts.length === 0 ? (
              <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Ainda não há tentativas registradas.</p>
            ) : detail.recentAttempts.slice(0, 15).map((attempt, index) => (
              <div key={attempt.id ?? String(index)} className="flex justify-between gap-2 border-b border-slate-100 pb-2 text-xs">
                <span className="text-slate-700">Aula {attempt.lessonId} · {attempt.scope === 'exercise' ? 'Exercício' : 'Avaliação'}</span>
                <span className="text-right text-slate-500">{formatDate(attempt.at, true)} · {attempt.result === 'mastered' ? 'Dominada' : attempt.result === 'clean' ? 'Correto' : attempt.result === 'incomplete' ? 'Incompleta' : 'Concluída'}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.accessLevel === 'admin';
  const [overview, setOverview] = useState<Overview | null>(null);
  const [list, setList] = useState<StudentList | null>(null);
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<StudentDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => { setOffset(0); setQuery(queryInput); }, 300);
    return () => window.clearTimeout(timer);
  }, [queryInput]);

  const reload = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [newOverview, newList] = await Promise.all([
        apiRequest<Overview>('/api/admin/summary'),
        apiRequest<StudentList>(`/api/admin/students?offset=${offset}&limit=${PAGE_SIZE}&q=${encodeURIComponent(query)}`),
      ]);
      setOverview(newOverview);
      setList(newList);
      setError('');
      setLastLoaded(new Date());
    } catch {
      setError('Não foi possível atualizar o painel. Confira a conexão e tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, offset, query]);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), 60000);
    return () => window.clearInterval(timer);
  }, [reload]);

  useEffect(() => {
    if (!selectedId || !isAdmin) { setDetail(null); return; }
    let cancelled = false;
    setLoadingDetail(true);
    setDetail(null);
    setDetailError('');
    apiRequest<StudentDetail>(`/api/admin/students/${selectedId}`)
      .then((result) => { if (!cancelled) setDetail(result); })
      .catch(() => { if (!cancelled) setDetailError('Não foi possível carregar os dados deste aluno.'); })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [isAdmin, selectedId]);

  const page = useMemo(() => Math.floor(offset / PAGE_SIZE) + 1, [offset]);
  if (!isAdmin) return <Navigate to="/aulas" replace />;

  return (
    <div className="flex min-h-screen flex-col bg-[#f7f8fc]">
      <SiteHeader />
      <main className="flex-1 pb-16">
        <div className="bg-[#111e37] px-4 py-9 text-white sm:px-8 md:py-12">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-orange-300">
                  <i className="ri-shield-user-line text-lg" /> Área restrita · Administração
                </div>
                <h1 className="mt-3 font-heading text-3xl font-extrabold md:text-4xl">Painel de alunos</h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-300">Acompanhe novos cadastros, evolução nas aulas, acertos, pontos e a trajetória de cada estudante.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/aulas" className="rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10">Ver aulas</Link>
                <button type="button" onClick={() => void reload()} className="rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-extrabold text-white hover:bg-orange-600">
                  <i className="ri-refresh-line mr-2" />Atualizar agora
                </button>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-slate-300">
              <span className="rounded-full bg-white/10 px-3 py-1.5"><i className="ri-lock-2-line mr-1" /> Somente administrador</span>
              <span>Atualização automática: 60 segundos</span>
              {lastLoaded && <span>Atualizado às {timestamps.format(lastLoaded)}</span>}
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-7xl space-y-6 px-4 py-7 sm:px-8">
          {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          {loading && !overview && <p className="rounded-2xl bg-white p-6 text-sm text-slate-600">Carregando informações da escola...</p>}
          {overview && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-4">
                <Metric label="Total de contas" value={overview.total} hint="Cadastros realizados" icon="ri-group-line" />
                <Metric label="Novos 7 dias" value={overview.registered7d} hint="Cadastros recentes" icon="ri-user-add-line" />
                <Metric label="Novos 30 dias" value={overview.registered30d} hint="Cadastros no mês" icon="ri-calendar-check-line" />
                <Metric label="Praticaram 7 dias" value={overview.practiced7d} hint="Com tentativa registrada" icon="ri-piano-line" />
                <Metric label="Concluíram curso" value={overview.courseCompleted} hint="48 aulas dominadas" icon="ri-medal-line" />
              </div>
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,1fr)]">
                <RegistrationChart points={overview.dailyRegistrations} />
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h2 className="font-heading text-lg font-extrabold text-slate-950">Visão do aprendizado</h2>
                  <p className="mt-1 text-sm text-slate-500">Resumo de participação da plataforma</p>
                  <div className="mt-6 flex items-center justify-between border-b border-slate-100 pb-4">
                    <div><div className="text-xs font-bold uppercase text-slate-500">Iniciaram exercícios</div><div className="mt-1 text-3xl font-extrabold text-slate-950">{overview.started}</div></div>
                    <span className="rounded-2xl bg-orange-50 p-3 text-2xl text-orange-600"><i className="ri-book-open-line" /></span>
                  </div>
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-orange-500" style={{ width: `${overview.total ? (overview.started / overview.total) * 100 : 0}%` }} />
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Os dados de atividade são baseados nas tentativas salvas, não apenas em entrar no site.</p>
                  <p className="mt-3 text-xs text-slate-500">A liberação especial da conta administradora não conta como aula concluída.</p>
                </div>
              </div>
            </>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl font-extrabold text-slate-950">Alunos cadastrados</h2>
                <p className="mt-1 text-sm text-slate-500">{list ? `${list.total} cadastro(s) encontrado(s)` : 'Consultando cadastros...'}</p>
              </div>
              <div className="relative w-full sm:w-80">
                <i className="ri-search-line absolute left-3 top-3.5 text-slate-400" />
                <input aria-label="Pesquisar aluno por nome ou e-mail" value={queryInput} onChange={(e) => setQueryInput(e.target.value)}
                  placeholder="Pesquisar nome ou e-mail..."
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-3 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" />
              </div>
            </div>
            <div className={`mt-5 grid gap-5 ${selectedId ? 'lg:grid-cols-[minmax(0,1fr)_minmax(350px,0.9fr)]' : ''}`}>
              <div className="space-y-3">
                {list?.items.length === 0 && <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">Nenhum aluno encontrado para esta pesquisa.</p>}
                {list?.items.map((student) => (
                  <StudentCard key={student.id} student={student} selected={selectedId === student.id} onOpen={() => setSelectedId(student.id)} />
                ))}
                {list && list.total > PAGE_SIZE && (
                  <div className="flex items-center justify-between gap-2 pt-3">
                    <button type="button" disabled={offset <= 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-40">Anterior</button>
                    <span className="text-sm text-slate-500">Página {page} de {Math.ceil(list.total / PAGE_SIZE)}</span>
                    <button type="button" disabled={offset + PAGE_SIZE >= list.total} onClick={() => setOffset(offset + PAGE_SIZE)}
                      className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-40">Próxima</button>
                  </div>
                )}
              </div>
              {selectedId && <StudentEvolution detail={detail} loading={loadingDetail} error={detailError} onClose={() => setSelectedId(null)} />}
            </div>
          </section>
          <p className="text-xs text-slate-500"><i className="ri-information-line mr-1" /> O painel é somente de leitura. Não altera resultados, senhas ou progresso dos alunos.</p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
